// // EliteStrategiesTradeLogs.js

// // ------------------------------------------------
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  forwardRef,
} from "react";

import { motion } from "framer-motion";

import Tooltip from "@mui/material/Tooltip";

import ShowChartIcon from "@mui/icons-material/ShowChart";
import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Pagination,
  useMediaQuery,
  Divider,
  IconButton,
  Chip,
  Card,
  CardContent,
  SwipeableDrawer,
  Select,
  MenuItem,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";

import {
  Area,
  AreaChart,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { keyframes } from "@mui/system";

import api from "../api";
import Loader from "./common/loader";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const STRATEGIES = {
  SS_RM_FRI_36_103_EOD_trades: {
    name: "SS RM FRI 36-103 EOD",
    shortName: "SS RM FRI 36-103 EOD",
    table: "SS_RM_FRI_36_103_EOD_trades",
    api: "/SS_RM_FRI_36_103_EOD_trades",
    startDate: "18 SEP 2026",
    group: "POSITIONAL",
    type: "SELL",
    straddle: true,
    icon: "trend",
    visibility: "public",
  },

  SS_SENSEX_TUE_HEDGE_EOD_RECENTER_NO_ADJ_trades: {
    name: "SS SENSEX TUE HEDGE EOD RECENTER NO ADJ",
    shortName: "SS SENSEX TUE HEDGE EOD RECENTER NO ADJ",
    table: "SS_SENSEX_TUE_HEDGE_EOD_RECENTER_NO_ADJ_trades",
    api: "/SS_SENSEX_TUE_HEDGE_EOD_RECENTER_NO_ADJ_trades",
    startDate: "13 OCT 2026",
    group: "POSITIONAL",
    type: "SELL",
    straddle: false,
    icon: "trend",
    visibility: "public",
  },
};

const isProd = process.env.NODE_ENV === "production";

const getStrategyIcon = (type) => {
  if (type === "candle") return <CandlestickChartIcon fontSize="small" />;
  return <ShowChartIcon fontSize="small" />;
};

const isMarketOpen = () => {
  const now = new Date();
  const istTime = new Date(
    now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }),
  );
  const day = istTime.getDay();
  if (day === 0 || day === 6) return false;
  const hours = istTime.getHours();
  const minutes = istTime.getMinutes();
  const currentMinutes = hours * 60 + minutes;
  const marketOpen = 9 * 60 + 15;
  const marketClose = 15 * 60 + 40;
  return currentMinutes >= marketOpen && currentMinutes <= marketClose;
};

const DateInput = forwardRef(
  ({ value, onClick, onChange, placeholder }, ref) => (
    <input
      ref={ref}
      value={value || ""}
      onClick={onClick}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: "100%",
        height: "42px",
        padding: "0 14px",
        borderRadius: "12px",
        border: "1px solid #d7dde5",
        background: "#fff",
        fontSize: "13px",
        fontWeight: 500,
        color: "#1f2937",
        outline: "none",
        boxSizing: "border-box",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      }}
    />
  ),
);

// Mobile Strategy Card Component
const MobileStrategyCard = ({
  strategyKey,
  strategy,
  active,
  tradeMode,
  onSelect,
  onToggle,
}) => (
  <Card
    onClick={() => onSelect(strategyKey)}
    sx={{
      mb: 1.5,
      borderRadius: 3,
      cursor: "pointer",
      background: active
        ? "linear-gradient(135deg, rgba(25,118,210,0.15), rgba(25,118,210,0.05))"
        : "rgba(255,255,255,0.8)",
      border: active ? "2px solid #1976d2" : "1px solid rgba(0,0,0,0.08)",
      boxShadow: active
        ? "0 4px 20px rgba(25,118,210,0.25)"
        : "0 2px 8px rgba(0,0,0,0.06)",
      transition: "all 0.2s ease",
      "&:active": {
        transform: "scale(0.98)",
      },
    }}
  >
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Chip
              size="small"
              label={strategy.type}
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 700,
                background:
                  strategy.type === "BUY"
                    ? "linear-gradient(135deg, #e8f5e9, #c8e6c9)"
                    : "linear-gradient(135deg, #ffebee, #ffcdd2)",
                color: strategy.type === "BUY" ? "#2e7d32" : "#c62828",
              }}
            />
            <Chip
              size="small"
              label={strategy.group}
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 600,
                background: "rgba(0,0,0,0.06)",
                color: "#666",
              }}
            />
          </Box>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: active ? "#1976d2" : "#333",
              lineHeight: 1.3,
            }}
          >
            {strategy.shortName || strategy.name}
          </Typography>
        </Box>
        <Box
          onClick={(e) => {
            e.stopPropagation();
            onToggle(strategyKey);
          }}
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            fontSize: 11,
            fontWeight: 700,
            background:
              tradeMode === 1
                ? "linear-gradient(135deg, #e8f5e9, #a5d6a7)"
                : "linear-gradient(135deg, #ffebee, #ef9a9a)",
            color: tradeMode === 1 ? "#1b5e20" : "#b71c1c",
            border: `1px solid ${tradeMode === 1 ? "#66bb6a" : "#ef5350"}`,
          }}
        >
          {tradeMode === 1 ? "PAPER" : "LIVE"}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// Mobile Trade Card Component
const MobileTradeCard = ({ trade, formatDateTime }) => {
  const isOpen =
    trade.status?.toLowerCase() === "open" ||
    trade.exit_time === null ||
    trade.exit_time === undefined;
  const pnl = Number(trade.pnl || 0);

  return (
    <Card
      sx={{
        mb: 1.5,
        borderRadius: 3,
        background: isOpen
          ? "linear-gradient(135deg, #fff8e1, #ffecb3)"
          : "rgba(255,255,255,0.9)",
        border: isOpen ? "1px solid #ffc107" : "1px solid rgba(0,0,0,0.08)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        {/* Header Row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              flexWrap: "wrap",
            }}
          >
            <Chip
              size="small"
              label={trade.option_type || "N/A"}
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 700,
                background:
                  trade.option_type === "CE"
                    ? "linear-gradient(135deg,#e8f5e9,#c8e6c9)"
                    : "linear-gradient(135deg,#ffebee,#ffcdd2)",
                color: trade.option_type === "CE" ? "#2e7d32" : "#c62828",
              }}
            />

            <Chip
              size="small"
              label={trade.action || trade.trade_type || "-"}
              sx={{
                height: 22,
                fontSize: 10,
                fontWeight: 700,
                background: "#e3f2fd",
                color: "#1565c0",
              }}
            />

            <Chip
              size="small"
              label={isOpen ? "OPEN" : "CLOSED"}
              sx={{
                height: 22,
                fontSize: 10,
                fontWeight: 600,
                background: isOpen ? "#fff3e0" : "#e8f5e9",
                color: isOpen ? "#e65100" : "#2e7d32",
              }}
            />
          </Box>

          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 700,
                color: "#888",
              }}
            >
              {isOpen ? "UNREALISED" : "REALISED"}
            </Typography>

            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: pnl > 0 ? "#2e7d32" : pnl < 0 ? "#c62828" : "#666",
              }}
            >
              {pnl > 0 ? "+" : ""}
              {pnl.toFixed(2)}
            </Typography>
          </Box>
        </Box>

        {trade.symbol && (
          <Box sx={{ mb: 1.5 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: "#073057",
                wordBreak: "break-all",
              }}
            >
              {trade.symbol}
            </Typography>
          </Box>
        )}

        {/* Details Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1,
          }}
        >
          {trade.strike && (
            <Box>
              <Typography sx={{ fontSize: 10, color: "#888", fontWeight: 600 }}>
                STRIKE
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
                {trade.strike}
              </Typography>
            </Box>
          )}
          {trade.quantity && (
            <Box>
              <Typography sx={{ fontSize: 10, color: "#888", fontWeight: 600 }}>
                QTY
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
                {trade.quantity}
              </Typography>
            </Box>
          )}
          {trade.entry_price && (
            <Box>
              <Typography sx={{ fontSize: 10, color: "#888", fontWeight: 600 }}>
                ENTRY
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
                {Number(trade.entry_price).toFixed(2)}
              </Typography>
            </Box>
          )}
          {trade.exit_price && (
            <Box>
              <Typography sx={{ fontSize: 10, color: "#888", fontWeight: 600 }}>
                EXIT
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
                {Number(trade.exit_price).toFixed(2)}
              </Typography>
            </Box>
          )}

          {isOpen && trade.current_ltp && (
            <Box>
              <Typography
                sx={{
                  fontSize: 10,
                  color: "#888",
                  fontWeight: 600,
                }}
              >
                LTP
              </Typography>

              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#1565c0",
                }}
              >
                {Number(trade.current_ltp).toFixed(2)}
              </Typography>
            </Box>
          )}
        </Box>

        {isOpen && (
          <Box
            sx={{
              mt: 1.5,
              p: 1,
              borderRadius: 2,
              background: "rgba(25,118,210,0.05)",
              border: "1px solid rgba(25,118,210,0.15)",
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: "#666",
              }}
            >
              UNREALISED P&L
            </Typography>

            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color:
                  Number(trade.unrealised_pnl || 0) > 0
                    ? "#2e7d32"
                    : Number(trade.unrealised_pnl || 0) < 0
                      ? "#c62828"
                      : "#666",
              }}
            >
              {Number(trade.unrealised_pnl || 0) > 0 ? "+" : ""}
              {Number(trade.unrealised_pnl || 0).toFixed(2)}
            </Typography>
          </Box>
        )}

        {/* Time Info */}
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
          <Typography sx={{ fontSize: 11, color: "#888" }}>
            {formatDateTime(trade.entry_time)}
            {trade.exit_time && ` → ${formatDateTime(trade.exit_time)}`}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// Desktop Sidebar Button
const SidebarButton = ({
  active,
  icon,
  label,
  sidebarOpen,
  onClick,
  tradeMode,
  onToggle,
  showToggle,
}) => {
  const textRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (!textRef.current) return;
    const el = textRef.current;
    const checkTruncation = () => {
      requestAnimationFrame(() => {
        if (!el) return;
        const isTruncated = el.scrollWidth > el.clientWidth + 1;
        setShowTooltip(isTruncated);
      });
    };
    checkTruncation();
    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [label, sidebarOpen]);

  const btn = (
    <Button
      onClick={onClick}
      disableRipple
      sx={{
        mb: 0.6,
        width: "100%",
        justifyContent: sidebarOpen ? "flex-start" : "center",
        px: sidebarOpen ? 1.5 : 0.5,
        py: 0.8,
        minHeight: 36,
        textTransform: "none",
        fontSize: 12,
        fontWeight: active ? 600 : 500,
        color: active ? "#1976d2" : "#444",
        borderRadius: 2,
        background: active
          ? "linear-gradient(135deg, rgba(25,118,210,0.25), rgba(25,118,210,0.08))"
          : "transparent",
        boxShadow: active ? "0 0 12px rgba(25,118,210,0.25)" : "none",
        transition: "all 0.2s ease",
        "&:hover": {
          transform: "translateX(2px) scale(1.02)",
          background:
            "linear-gradient(135deg, rgba(25,118,210,0.12), rgba(25,118,210,0.04))",
          boxShadow: "0 4px 12px rgba(25,118,210,0.15)",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
        }}
      >
        {active && (
          <Box
            sx={{
              position: "absolute",
              left: -6,
              top: "50%",
              transform: "translateY(-50%)",
              width: 3,
              height: "60%",
              borderRadius: 2,
              background: "#1976d2",
              boxShadow: "0 0 8px rgba(25,118,210,0.6)",
            }}
          />
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            px: 1.5,
            gap: 1.2,
            transition: "all 200ms ease",
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          {React.cloneElement(icon, {
            fontSize: "small",
            sx: {
              color: active ? "#1976d2" : "#888",
              transition: "all 0.2s ease",
              ".MuiButton-root:hover &": {
                color: "#1976d2",
              },
            },
          })}
          {sidebarOpen && (
            <Typography
              ref={textRef}
              component="span"
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: active ? "#cc5d08" : "#1e3a5f",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: 1,
                minWidth: 0,
                opacity: sidebarOpen ? 1 : 0,
                transform: sidebarOpen ? "translateX(0)" : "translateX(-8px)",
                pointerEvents: sidebarOpen ? "auto" : "none",
                transition: `opacity 200ms ease, transform 260ms cubic-bezier(0.22,1,0.36,1)`,
              }}
            >
              {label}
            </Typography>
          )}
        </Box>
        {sidebarOpen && showToggle && tradeMode !== undefined && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onToggle?.();
            }}
            sx={{
              px: 1.2,
              py: "3px",
              fontSize: 10,
              fontWeight: 700,
              borderRadius: 999,
              cursor: "pointer",
              minWidth: 60,
              textAlign: "center",
              background:
                tradeMode === 1
                  ? "linear-gradient(135deg, #e8f5e9, #c8e6c9)"
                  : "linear-gradient(135deg, #fdecea, #ffcdd2)",
              color: tradeMode === 1 ? "#1b5e20" : "#b71c1c",
              border: "1px solid",
              borderColor: tradeMode === 1 ? "#66bb6a" : "#ef5350",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
          >
            {tradeMode === 1 ? "PAPER" : "LIVE"}
          </Box>
        )}
      </Box>
    </Button>
  );

  if (!showTooltip) return btn;

  return (
    <Tooltip
      title={
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#111" }}>
          {label}
        </Typography>
      }
      placement={sidebarOpen ? "top" : "right"}
      followCursor={sidebarOpen}
      arrow
      enterDelay={300}
      leaveDelay={100}
    >
      <span>{btn}</span>
    </Tooltip>
  );
};

const TimeSelector = ({ value, onChange }) => {
  const hour = value?.getHours() ?? 0;
  const minute = value?.getMinutes() ?? 0;

  const updateHour = (newHour) => {
    const d = new Date(value);
    d.setHours(Number(newHour));
    onChange(d);
  };

  const updateMinute = (newMinute) => {
    const d = new Date(value);
    d.setMinutes(Number(newMinute));
    onChange(d);
  };

  const selectStyle = {
    minWidth: 60,
    height: 42,
    borderRadius: 3,
    background: "#fff",

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#d7dde5",
    },

    "& .MuiSelect-select": {
      fontSize: 13,
      fontWeight: 500,
      display: "flex",
      alignItems: "center",
    },
  };

  return (
    <>
      <FormControl size="small">
        <Select
          value={hour}
          onChange={(e) => updateHour(e.target.value)}
          sx={selectStyle}
        >
          {Array.from({ length: 24 }, (_, i) => (
            <MenuItem key={i} value={i}>
              {String(i).padStart(2, "0")}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small">
        <Select
          value={minute}
          onChange={(e) => updateMinute(e.target.value)}
          sx={selectStyle}
        >
          {Array.from({ length: 60 }, (_, i) => (
            <MenuItem key={i} value={i}>
              {String(i).padStart(2, "0")}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
};

function EliteStrategiesTradeLogs() {
  const [exiting, setExiting] = useState(false);
  const [convertToPaperAfterExit, setConvertToPaperAfterExit] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [tradeModes, setTradeModes] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  const [niftyData, setNiftyData] = useState({
    ltp: null,
    state: null,
    ws_connected: null,
    last_updated_at: null,
  });

  const [straddleAnalyticsOpen, setStraddleAnalyticsOpen] = useState(true);

  const [straddleAnalytics, setStraddleAnalytics] = useState(null);
  const [straddleAnalyticsLoading, setStraddleAnalyticsLoading] =
    useState(false);

  const [activeStrategy, setActiveStrategy] = useState(null);

  const [trades, setTrades] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const [userId, setUserId] = useState(() => {
    return Number(localStorage.getItem("user_id"));
  });

  const filteredStrategies = React.useMemo(() => {
    return Object.fromEntries(
      Object.entries(STRATEGIES).filter(([_, s]) => {
        if (!isProd) return true;

        if (userId === 7 || userId === 9) return true;

        return s.visibility !== "private";
      }),
    );
  }, [userId]);

  const getDefaultFromDate = () => {
    const d = new Date(2026, 0, 1);
    d.setHours(9, 15, 0, 0); // 9:15:00
    return d;
  };

  const [fromDate, setFromDate] = useState(getDefaultFromDate());

  const getDefaultToDate = () => {
    const d = new Date();
    d.setHours(15, 40, 0, 0); // 15:40:00
    return d;
  };

  const [toDate, setToDate] = useState(getDefaultToDate());

  const fromDateRef = useRef(fromDate);
  const toDateRef = useRef(toDate);
  const [isPickingDate, setIsPickingDate] = useState(false);

  const [tableOpen, setTableOpen] = useState(true);
  const [chartOpen, setChartOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [dailyChartOpen, setDailyChartOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [mobileViewMode, setMobileViewMode] = useState("cards"); // 'cards' or 'table'

  const limit = 1000;
  const isMobile = useMediaQuery("(max-width:600px)");
  const tableRef = useRef(null);
  const scrollRef = useRef(null);
  const scrollXDirRef = useRef(0);
  const scrollYDirRef = useRef(0);
  const hoverTimeoutRef = useRef(null);

  const EDGE_THRESHOLD = 60;
  const SCROLL_SPEED_X = 12;
  const SCROLL_SPEED_Y = 10;

  useEffect(() => {
    const handleStorageChange = () => {
      setUserId(Number(localStorage.getItem("user_id")));
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const keys = Object.keys(filteredStrategies);

    if (!keys.length) return;

    setActiveStrategy((prev) => {
      // keep existing if still valid
      if (prev && keys.includes(prev)) return prev;

      // else pick first
      return keys[0];
    });
  }, [filteredStrategies]);

  useEffect(() => {
    fromDateRef.current = fromDate;
    toDateRef.current = toDate;
  }, [fromDate, toDate]);

  const startAutoScroll = (dx, dy) => {
    if (scrollXDirRef.current === dx && scrollYDirRef.current === dy) return;
    stopAutoScroll();
    scrollXDirRef.current = dx;
    scrollYDirRef.current = dy;
    const step = () => {
      if (!tableRef.current || document.hidden) return;
      tableRef.current.scrollLeft += dx * SCROLL_SPEED_X;
      tableRef.current.scrollTop += dy * SCROLL_SPEED_Y;
      scrollRef.current = requestAnimationFrame(step);
    };
    scrollRef.current = requestAnimationFrame(step);
  };

  const stopAutoScroll = () => {
    scrollXDirRef.current = 0;
    scrollYDirRef.current = 0;
    if (scrollRef.current) {
      cancelAnimationFrame(scrollRef.current);
      scrollRef.current = null;
    }
  };

  const handleMouseMove = (e) => {
    if (isMobile) return;
    const el = tableRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    let dx = 0;
    let dy = 0;
    if (x < EDGE_THRESHOLD) dx = -1;
    else if (x > rect.width - EDGE_THRESHOLD) dx = 1;
    if (y < EDGE_THRESHOLD) dy = -1;
    else if (y > rect.height - EDGE_THRESHOLD) dy = 1;
    if (dx !== 0 || dy !== 0) {
      startAutoScroll(dx, dy);
    } else {
      stopAutoScroll();
    }
  };

  const exitLiveTrades = async () => {
    if (!activeStrategy) return;
    const confirm = window.confirm("Exit all LIVE trades?");
    if (!confirm) return;
    try {
      setExiting(true);
      const dbStrategy = STRATEGIES[activeStrategy]?.table;
      await api.post("/exit-live-trades", {
        strategy: dbStrategy,
        convert_to_paper: convertToPaperAfterExit,
      });
      fetchStrategyTrades();
    } catch (err) {
      console.error("Exit trades failed", err);
    } finally {
      setExiting(false);
    }
  };

  const fetchStraddleAnalytics = useCallback(async () => {
    if (!activeStrategy) return;

    if (!filteredStrategies[activeStrategy]?.straddle) return;

    try {
      setStraddleAnalyticsLoading(true);

      const strategy = filteredStrategies[activeStrategy]?.table;

      const res = await api.get("/straddle-strategy-analysis-report", {
        params: {
          strategy,
        },
      });

      setStraddleAnalytics(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setStraddleAnalyticsLoading(false);
    }
  }, [activeStrategy, filteredStrategies]);

  const fetchTradeMode = useCallback(async (uiStrategy) => {
    const dbStrategy = STRATEGIES[uiStrategy]?.table;
    if (!dbStrategy) return;
    try {
      const res = await api
        .get("/check-trade-type", {
          params: { strategy: dbStrategy },
        })
        .catch((err) => {
          console.error(err);
          return { data: { data: [], totalCount: 0 } };
        });
      setTradeModes((prev) => ({
        ...prev,
        [uiStrategy]: res.data.paper_trade,
      }));
    } catch (err) {
      console.error("Failed to fetch trade mode", err);
    }
  }, []);

  useEffect(() => {
    if (activeStrategy) {
      fetchTradeMode(activeStrategy);
    }
  }, [activeStrategy, fetchTradeMode]);

  useEffect(() => {
    const loadModes = async () => {
      const entries = Object.keys(filteredStrategies);
      await Promise.all(entries.map(fetchTradeMode));
    };

    if (Object.keys(filteredStrategies).length) {
      loadModes();
    }
  }, [filteredStrategies, fetchTradeMode]);

  const toggleTradeMode = async (uiStrategy) => {
    if (toggling) return;
    if (tradeModes[uiStrategy] === 1) {
      const ok = window.confirm("Switching to LIVE trading. Are you sure?");
      if (!ok) return;
    }
    if (tradeModes[uiStrategy] === 0) {
      const ok = window.confirm("Switching to Paper trading. Are you sure?");
      if (!ok) return;
    }
    const dbStrategy = STRATEGIES[uiStrategy]?.table;
    if (!dbStrategy) return;
    try {
      setToggling(true);
      const nextPaperTrade = tradeModes[uiStrategy] === 1 ? 0 : 1;
      await api.get("/toggle-trade-type", {
        params: {
          strategy: dbStrategy,
          paper_trade: nextPaperTrade,
        },
      });
      setTradeModes((prev) => ({
        ...prev,
        [uiStrategy]: prev[uiStrategy] === 1 ? 0 : 1,
      }));
    } catch (err) {
      console.error("Toggle failed", err);
    } finally {
      setToggling(false);
    }
  };

  const fetchStrategyTrades = useCallback(async () => {
    if (!activeStrategy) return;

    const table = filteredStrategies[activeStrategy]?.table;
    if (!table) return;

    if (trades.length === 0) {
      setLoading(true);
    }

    try {
      const offset = (page - 1) * limit;

      const res = await api.get(`/trades/${table}`, {
        params: {
          fromDate: formatDateTime(fromDateRef.current),
          toDate: formatDateTime(toDateRef.current),
          limit,
          offset,
        },
      });

      setTrades(res.data.data || []);
      setTotalCount(res.data.totalCount || 0);
    } catch (err) {
      console.error("Error fetching strategy logs:", err);
    } finally {
      setLoading(false);
    }
  }, [activeStrategy, page, limit, trades.length, filteredStrategies]);

  const fetchNiftySpot = useCallback(async () => {
    try {
      const res = await api.get("/nifty-spot");

      setNiftyData({
        ltp: res.data.ltp,
        state: res.data.state,
        ws_connected: res.data.ws_connected,
        last_updated_at: res.data.last_updated_at,
      });
    } catch (err) {
      console.error("Failed to fetch NIFTY spot", err);
    }
  }, []);

  useEffect(() => {
    fetchNiftySpot();

    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchNiftySpot();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchNiftySpot]);

  useEffect(() => {
    if (!activeStrategy) return;
    setPage(1);
  }, [fromDate, toDate, activeStrategy]);

  useEffect(() => {
    if (!activeStrategy) return;

    fetchStrategyTrades();

    if (filteredStrategies[activeStrategy]?.straddle) {
      fetchStraddleAnalytics();
    }
  }, [
    activeStrategy,
    fromDate,
    toDate,
    page,
    fetchStrategyTrades,
    fetchStraddleAnalytics,
    filteredStrategies,
  ]);

  const hasOpenTrades = trades.some(
    (t) =>
      t.status?.toLowerCase() === "open" ||
      t.exit_time === null ||
      t.exit_time === undefined,
  );

  const openTradesCount = trades.filter(
    (t) =>
      t.status?.toLowerCase() === "open" ||
      t.exit_time === null ||
      t.exit_time === undefined,
  ).length;

  useEffect(() => {
    if (!activeStrategy) return;
    if (!hasOpenTrades) return;

    const interval = setInterval(() => {
      if (
        document.visibilityState === "visible" &&
        isMarketOpen() &&
        !isPickingDate
      ) {
        fetchStrategyTrades();

        // if (filteredStrategies[activeStrategy]?.straddle) {
        //   fetchStraddleAnalytics();
        // }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [
    activeStrategy,
    hasOpenTrades,
    fetchStrategyTrades,
    fetchStraddleAnalytics,
    filteredStrategies,
    isPickingDate,
  ]);

  const formatDateTime = (input) => {
    if (!input) return "-";

    const date = new Date(input); // handles both string & Date

    if (isNaN(date)) return "-";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const formatDateTimeShort = (ts) => {
    if (!ts) return "-";
    const date = new Date(ts);
    return date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const VirtualRow = ({ index, style, data }) => {
    const row = data.rows[index];
    const columns = data.columns;
    const formatDateTime = data.formatDateTime;
    return (
      <TableRow style={style}>
        {columns.map((key) => (
          <TableCell key={key}>
            {key.includes("time") && row[key]
              ? formatDateTime(row[key])
              : (row[key] ?? "-")}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  const pnlChartData = React.useMemo(() => {
    return [...trades].reverse().reduce((acc, trade, index) => {
      const pnl = Number(trade.pnl || 0);
      const prev = acc.length ? acc[acc.length - 1].cumulativePnl : 0;
      const cumulative = prev + pnl;
      acc.push({
        index: index + 1,
        time: trade.entry_time || trade.created_at || "",
        pnl,
        cumulativePnl: cumulative,
      });
      return acc;
    }, []);
  }, [trades]);

  const dailyPnlData = React.useMemo(() => {
    if (!trades.length) return [];
    const map = {};
    trades.forEach((t) => {
      const date = (t.entry_time || t.created_at || "").slice(0, 10);
      if (!date) return;
      const pnl = Number(t.pnl || 0);
      if (!map[date]) map[date] = 0;
      map[date] += pnl;
    });
    return Object.entries(map)
      .sort((a, b) => new Date(a[0]) - new Date(b[0]))
      .map(([date, pnl]) => ({
        date,
        pnl,
      }));
  }, [trades]);

  const hasUnrealisedColumn =
    trades.length > 0 &&
    Object.prototype.hasOwnProperty.call(trades[0], "unrealised_pnl");

  const totalUnrealisedPnl = hasUnrealisedColumn
    ? trades.reduce((sum, t) => {
        const isOpen =
          t.status?.toLowerCase() === "open" ||
          t.exit_time === null ||
          t.exit_time === undefined;
        if (!isOpen) return sum;
        return sum + Number(t.unrealised_pnl || 0);
      }, 0)
    : 0;

  const netOptionLtp = trades.reduce((sum, t) => {
    if (!t.pnl || !t.quantity) return sum;
    return sum + Number(t.pnl) / Number(t.quantity);
  }, 0);

  const totalSpotGain = trades.reduce((sum, t) => {
    const entry = Number(t.spot_entry);
    const exit = Number(t.spot_exit);
    if (!entry || !exit) return sum;
    if (t.option_type?.toUpperCase() === "CE") {
      return sum + (exit - entry);
    }
    if (t.option_type?.toUpperCase() === "PE") {
      return sum + (entry - exit);
    }
    return sum;
  }, 0);

  const niftyOptionRatio =
    Math.abs(totalSpotGain) > 0
      ? ((Math.abs(netOptionLtp) / Math.abs(totalSpotGain)) * 100).toFixed(2)
      : "0.00";

  const isBuyStrategy = filteredStrategies[activeStrategy]?.type === "BUY";

  const analytics = React.useMemo(() => {
    if (!trades.length) return null;
    const closedTrades = trades.filter(
      (t) =>
        t.status?.toLowerCase() === "closed" ||
        (t.exit_time !== null && t.exit_time !== undefined),
    );
    if (!closedTrades.length) return null;
    const pnls = closedTrades.map((t) => Number(t.pnl || 0));
    const wins = pnls.filter((p) => p > 0);
    const losses = pnls.filter((p) => p < 0);
    const winRate = (wins.length / pnls.length) * 100;
    const avgWin =
      wins.length > 0 ? wins.reduce((a, b) => a + b, 0) / wins.length : 0;
    const avgLoss =
      losses.length > 0 ? losses.reduce((a, b) => a + b, 0) / losses.length : 0;
    const profitFactor =
      Math.abs(losses.reduce((a, b) => a + b, 0)) > 0
        ? wins.reduce((a, b) => a + b, 0) /
          Math.abs(losses.reduce((a, b) => a + b, 0))
        : 0;
    let equity = 0;
    let peak = 0;
    let maxDD = 0;
    pnls.forEach((p) => {
      equity += p;
      peak = Math.max(peak, equity);
      maxDD = Math.min(maxDD, equity - peak);
    });
    const mean = pnls.reduce((a, b) => a + b, 0) / pnls.length;
    const variance =
      pnls.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / pnls.length;
    const stdDev = Math.sqrt(variance);
    const sharpe = stdDev !== 0 ? (mean / stdDev) * Math.sqrt(pnls.length) : 0;
    return {
      winRate,
      avgWin,
      avgLoss,
      profitFactor,
      maxDrawdown: maxDD,
      sharpe,
    };
  }, [trades]);

  const tradeInsights = React.useMemo(() => {
    if (!trades.length) return null;
    const closed = trades.filter(
      (t) =>
        t.status?.toLowerCase() === "closed" ||
        (t.exit_time !== null && t.exit_time !== undefined),
    );
    if (!closed.length) return null;
    const pnls = closed.map((t) => Number(t.pnl || 0));
    const bestTrade = Math.max(...pnls);
    const worstTrade = Math.min(...pnls);
    let winStreak = 0;
    let lossStreak = 0;
    let currentWin = 0;
    let currentLoss = 0;
    pnls.forEach((p) => {
      if (p > 0) {
        currentWin++;
        currentLoss = 0;
      } else if (p < 0) {
        currentLoss++;
        currentWin = 0;
      }
      winStreak = Math.max(winStreak, currentWin);
      lossStreak = Math.max(lossStreak, currentLoss);
    });
    return {
      bestTrade,
      worstTrade,
      winStreak,
      lossStreak,
    };
  }, [trades]);

  const handleRefresh = async () => {
    if (!activeStrategy) return;

    setLoading(true);
    setRefreshing(true);

    try {
      await fetchStrategyTrades();

      if (filteredStrategies[activeStrategy]?.straddle) {
        await fetchStraddleAnalytics();
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSidebarEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setSidebarOpen(true);
    }, 180);
  };

  const handleSidebarLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setSidebarOpen(false);
    }, 320);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Mobile Drawer Content
  const MobileDrawerContent = () => (
    <Box
      sx={{
        width: "85vw",
        maxWidth: 360,
        height: "100%",
        background: "linear-gradient(180deg, #f8faff 0%, #e8f0fe 100%)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Drawer Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "rgba(255,255,255,0.9)",
        }}
      >
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: "#1976d2",
          }}
        >
          📊 Strategies
        </Typography>
        <IconButton onClick={() => setMobileDrawerOpen(false)} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Strategy List */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2,
          WebkitOverflowScrolling: "touch",
        }}
      >
        {["POSITIONAL", "INTRADAY"].map((group) => {
          const groupStrategies = Object.entries(filteredStrategies).filter(
            ([, s]) => s.group === group,
          );
          if (!groupStrategies.length) return null;

          return (
            <Box key={group} sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#1976d2",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  mb: 1.5,
                  px: 0.5,
                }}
              >
                {group}
              </Typography>

              {groupStrategies.map(([key, strategy]) => (
                <MobileStrategyCard
                  key={key}
                  strategyKey={key}
                  strategy={strategy}
                  active={activeStrategy === key}
                  tradeMode={tradeModes[key]}
                  onSelect={(k) => {
                    setActiveStrategy(k);
                    setPage(1);
                    setMobileDrawerOpen(false);
                  }}
                  onToggle={toggleTradeMode}
                />
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  const getNiftyStatus = () => {
    const now = new Date();

    const ist = new Date(
      now.toLocaleString("en-US", {
        timeZone: "Asia/Kolkata",
      }),
    );

    const day = ist.getDay();
    const mins = ist.getHours() * 60 + ist.getMinutes();

    const marketOpen = day >= 1 && day <= 5 && mins >= 555 && mins <= 940;

    if (!marketOpen) {
      return {
        color: "#f57c00",
        label: "Market Closed",
      };
    }

    if (niftyData.ws_connected === 1 && niftyData.state === 1) {
      return {
        color: "#2e7d32",
        label: "Live",
      };
    }

    return {
      color: "#c62828",
      label: "Disconnected",
    };
  };

  // ========== RENDER ==========
  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        animation: `${fadeIn} 0.4s ease`,
        background: `
          radial-gradient(circle at 20% 20%, rgba(13, 95, 177, 0.15), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(0,200,83,0.12), transparent 40%),
          radial-gradient(circle at 50% 80%, rgba(255,82,82,0.10), transparent 40%),
          linear-gradient(135deg, #cdd4f8, #edf7d6)
        `,
      }}
    >
      {/* MOBILE DRAWER */}
      {isMobile && (
        <SwipeableDrawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          onOpen={() => setMobileDrawerOpen(true)}
          swipeAreaWidth={30}
          disableSwipeToOpen={false}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              borderRadius: "0 20px 20px 0",
              overflow: "hidden",
            },
          }}
        >
          <MobileDrawerContent />
        </SwipeableDrawer>
      )}

      {/* DESKTOP SIDEBAR */}
      {!isMobile && (
        <motion.div
          id="sidebar"
          onMouseEnter={handleSidebarEnter}
          onMouseLeave={handleSidebarLeave}
          animate={{
            width: sidebarOpen ? 500 : 64,
            scale: sidebarOpen ? 1 : 0.995,
            boxShadow: sidebarOpen
              ? "0 12px 40px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.6)"
              : "0 6px 20px rgba(0,0,0,0.10), inset 0 1px 0 rgba(255,255,255,0.6)",
          }}
          initial={false}
          transition={{
            type: "spring",
            stiffness: 120,
            damping: 18,
            mass: 0.6,
          }}
          style={{
            flex: "0 0 auto",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            transform: "translateZ(0)",
            willChange: "width",
            background: "rgba(189, 222, 245, 0.55)",
            backdropFilter: "blur(18px)",
            borderRight: "1px solid rgba(255,255,255,0.3)",
          }}
        >
          {/* Desktop Sidebar Header */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              background: "rgba(255,255,255,0.6)",
              backdropFilter: "blur(16px)",
              borderBottom: "1px solid rgba(255,255,255,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1,
            }}
          >
            <Typography
              sx={{
                opacity: sidebarOpen ? 1 : 0,
                transition: "opacity 200ms ease",
                fontSize: 12,
                fontWeight: 700,
                color: "#1976d2",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
              }}
            >
              STRATEGIES
            </Typography>
            <Button
              size="small"
              sx={{
                minWidth: 32,
                width: 32,
                height: 32,
                borderRadius: 1.5,
                fontWeight: "bold",
                background: "rgba(255,255,255,0.6)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.4)",
                "&:hover": {
                  background: "rgba(255,255,255,0.8)",
                  transform: "scale(1.05)",
                },
              }}
              onClick={() => setSidebarOpen((prev) => !prev)}
            >
              {sidebarOpen ? "←" : "☰"}
            </Button>
          </Box>

          {/* Desktop Strategy List */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              px: 2,
              py: 1,
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(0,0,0,0.15)",
                borderRadius: 3,
              },
            }}
          >
            {["POSITIONAL", "INTRADAY"].map((group) => {
              const groupStrategies = Object.entries(filteredStrategies).filter(
                ([, s]) => s.group === group,
              );
              if (!groupStrategies.length) return null;
              const buyStrategies = groupStrategies.filter(
                ([, s]) => s.type === "BUY",
              );
              const sellStrategies = groupStrategies.filter(
                ([, s]) => s.type === "SELL",
              );

              return (
                <Box key={group}>
                  <Typography
                    sx={{
                      mt: 2,
                      mb: 0.5,
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#1976d2",
                      textTransform: "uppercase",
                      letterSpacing: "0.12em",
                      opacity: sidebarOpen ? 1 : 0,
                      transition: "opacity 200ms ease",
                    }}
                  >
                    {group}
                  </Typography>

                  {buyStrategies.length > 0 && (
                    <>
                      <Typography
                        sx={{
                          mt: 1,
                          mb: 0.5,
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#2e7d32",
                          opacity: sidebarOpen ? 1 : 0,
                          transition: "opacity 200ms ease",
                        }}
                      >
                        BUY
                      </Typography>
                      {buyStrategies.map(([key, strategy]) => (
                        <SidebarButton
                          key={key}
                          active={activeStrategy === key}
                          sidebarOpen={sidebarOpen}
                          icon={getStrategyIcon(strategy.icon)}
                          label={strategy.name}
                          tradeMode={tradeModes[key]}
                          showToggle
                          onToggle={() => toggleTradeMode(key)}
                          onClick={() => {
                            setPage(1);
                            setActiveStrategy(key);
                          }}
                        />
                      ))}
                    </>
                  )}

                  {sellStrategies.length > 0 && (
                    <>
                      <Typography
                        sx={{
                          mt: 1.5,
                          mb: 0.5,
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#c62828",
                          opacity: sidebarOpen ? 1 : 0,
                          transition: "opacity 200ms ease",
                        }}
                      >
                        SELL
                      </Typography>
                      {sellStrategies.map(([key, strategy]) => (
                        <SidebarButton
                          key={key}
                          active={activeStrategy === key}
                          sidebarOpen={sidebarOpen}
                          icon={getStrategyIcon(strategy.icon)}
                          label={strategy.name}
                          tradeMode={tradeModes[key]}
                          showToggle
                          onToggle={() => toggleTradeMode(key)}
                          onClick={() => {
                            setPage(1);
                            setActiveStrategy(key);
                          }}
                        />
                      ))}
                    </>
                  )}

                  <Divider
                    sx={{
                      my: 1.5,
                      opacity: 0.3,
                      background:
                        "linear-gradient(to right, transparent, #ccc, transparent)",
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </motion.div>
      )}

      {/* MAIN CONTENT */}
      <Box
        sx={{
          flex: 1,
          p: isMobile ? 1.5 : 2,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {/* Mobile Header Bar */}
        {isMobile && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              p: 1.5,
              borderRadius: 3,
              background: "rgba(255,255,255,0.8)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.4)",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <IconButton
              onClick={() => setMobileDrawerOpen(true)}
              sx={{
                background: "linear-gradient(135deg, #1976d2, #42a5f5)",
                color: "#fff",
                "&:hover": {
                  background: "linear-gradient(135deg, #1565c0, #1976d2)",
                },
              }}
            >
              <MenuIcon />
            </IconButton>

            <Box sx={{ flex: 1, mx: 1.5, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#073057",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {filteredStrategies[activeStrategy]?.shortName ||
                  filteredStrategies[activeStrategy]?.name}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* Trade Mode Badge */}
              <Chip
                size="small"
                label={tradeModes[activeStrategy] === 1 ? "PAPER" : "LIVE"}
                onClick={() => toggleTradeMode(activeStrategy)}
                sx={{
                  height: 26,
                  fontSize: 10,
                  fontWeight: 700,
                  background:
                    tradeModes[activeStrategy] === 1
                      ? "linear-gradient(135deg, #e8f5e9, #a5d6a7)"
                      : "linear-gradient(135deg, #ffebee, #ef9a9a)",
                  color:
                    tradeModes[activeStrategy] === 1 ? "#1b5e20" : "#b71c1c",
                  border: `1px solid ${
                    tradeModes[activeStrategy] === 1 ? "#66bb6a" : "#ef5350"
                  }`,
                }}
              />

              {/* Refresh */}
              <IconButton
                size="small"
                onClick={handleRefresh}
                sx={{
                  background: "rgba(255,255,255,0.8)",
                  border: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <Box
                  component="img"
                  src="/refreshIcon1.png"
                  alt="refresh"
                  sx={{
                    width: 16,
                    height: 16,
                    animation: refreshing ? "spin 1s linear infinite" : "none",
                    "@keyframes spin": {
                      from: { transform: "rotate(0deg)" },
                      to: { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </IconButton>
            </Box>
          </Box>
        )}

        {activeStrategy && (
          <Box
            sx={{
              position: isMobile ? "relative" : "sticky",
              top: isMobile ? "auto" : 0,
              zIndex: 100,
              pb: 1,
              backdropFilter: "blur(12px)",
            }}
          >
            {/* Desktop Strategy Header */}
            {!isMobile && activeStrategy && (
              <Box
                sx={{
                  mb: 2,
                  px: 2,
                  py: 1.5,

                  borderRadius: 3,

                  background: "rgba(255,255,255,0.85)",
                  backdropFilter: "blur(16px)",
                  WebkitBackdropFilter: "blur(16px)",

                  border: "1px solid rgba(255,255,255,0.4)",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",

                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: "#073057",
                    }}
                  >
                    📊{" "}
                    {filteredStrategies[activeStrategy]?.name || activeStrategy}
                  </Typography>

                  {filteredStrategies[activeStrategy] && (
                    <Button
                      size="small"
                      disabled={tradeModes[activeStrategy] === undefined}
                      onClick={() => toggleTradeMode(activeStrategy)}
                      sx={{
                        height: 26,
                        px: 1.6,
                        fontSize: 11,
                        fontWeight: 800,
                        borderRadius: 999,
                        textTransform: "none",
                        transition: "all 0.2s ease",
                        background:
                          tradeModes[activeStrategy] === 1
                            ? "linear-gradient(135deg, #e8f5e9, #a5d6a7)"
                            : tradeModes[activeStrategy] === 0
                              ? "linear-gradient(135deg, #fdecea, #ef9a9a)"
                              : "rgba(255,255,255,0.6)",
                        color:
                          tradeModes[activeStrategy] === 1
                            ? "#1b5e20"
                            : tradeModes[activeStrategy] === 0
                              ? "#b71c1c"
                              : "#666",
                        border: "1px solid",
                        borderColor:
                          tradeModes[activeStrategy] === 1
                            ? "#66bb6a"
                            : tradeModes[activeStrategy] === 0
                              ? "#ef5350"
                              : "rgba(255,255,255,0.4)",
                        boxShadow:
                          tradeModes[activeStrategy] === 1
                            ? "0 4px 12px rgba(46,125,50,0.35)"
                            : tradeModes[activeStrategy] === 0
                              ? "0 4px 12px rgba(198,40,40,0.35)"
                              : "0 2px 6px rgba(0,0,0,0.1)",
                        "&:hover": {
                          transform: "scale(1.06)",
                        },
                      }}
                    >
                      {tradeModes[activeStrategy] === undefined
                        ? "..."
                        : tradeModes[activeStrategy] === 1
                          ? "PAPER"
                          : "LIVE"}
                    </Button>
                  )}
                </Box>

                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {tradeModes[activeStrategy] === 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mr: 1,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={convertToPaperAfterExit}
                        disabled={exiting}
                        onChange={(e) =>
                          setConvertToPaperAfterExit(e.target.checked)
                        }
                      />
                      <Typography sx={{ fontSize: 12 }}>
                        Convert to PAPER
                      </Typography>
                    </Box>
                  )}

                  {tradeModes[activeStrategy] === 0 && (
                    <Button
                      size="small"
                      disabled={exiting}
                      onClick={exitLiveTrades}
                      sx={{
                        height: 36,
                        px: 2,
                        fontSize: 12,
                        fontWeight: 700,
                        borderRadius: 999,
                        background: "rgba(198,40,40,0.15)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(198,40,40,0.4)",
                        color: "#b71c1c",
                        "&:hover": {
                          background: "rgba(198,40,40,0.25)",
                          transform: "scale(1.05)",
                        },
                      }}
                    >
                      {exiting ? "EXITING…" : "EXIT TRADES"}
                    </Button>
                  )}

                  {openTradesCount > 0 && (
                    <Box
                      sx={{
                        px: 1.2,
                        height: 30,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 999,
                        fontSize: 14,
                        fontWeight: 600,
                        background: "linear-gradient(135deg, #fbe6bd, #fbe6bd)",
                        color: "#000056",
                        border: "1px solid rgba(0, 47, 255, 0.4)",
                        boxShadow: "0 2px 8px rgba(0, 136, 255, 0.25)",
                        animation: "pulse 1.5s infinite",
                        "@keyframes pulse": {
                          "0%": { transform: "scale(1)" },
                          "50%": { transform: "scale(1.08)" },
                          "100%": { transform: "scale(1)" },
                        },
                      }}
                    >
                      Open: {openTradesCount}
                    </Box>
                  )}

                  <Button
                    size="small"
                    variant="outlined"
                    disabled={exiting}
                    onClick={handleRefresh}
                    sx={{
                      minWidth: 36,
                      height: 36,
                      borderRadius: "50%",
                      padding: 0,
                      background: "rgba(255,255,255,0.6)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255,255,255,0.4)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        background: "rgba(255,255,255,0.8)",
                        transform: "scale(1.1)",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src="/refreshIcon1.png"
                      alt="refresh"
                      sx={{
                        width: 18,
                        height: 18,
                        opacity: 0.9,

                        animation: refreshing
                          ? "spin 1s linear infinite"
                          : "none",

                        "@keyframes spin": {
                          from: { transform: "rotate(0deg)" },
                          to: { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                  </Button>
                </Box>
              </Box>
            )}

            {/* Date Filters */}
            {activeStrategy && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  justifyContent: "space-between",
                  alignItems: isMobile ? "stretch" : "center",
                  mb: 2,
                  gap: 2,
                }}
              >
                {/* LEFT SIDE - DATE PICKERS */}
                <Box
                  sx={{
                    display: "flex",
                    gap: isMobile ? 2 : 0,
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: "center",
                    ml: isMobile ? 0 : 10, // move date filters to the right
                  }}
                >
                  {/* FROM DATE */}
                  <Box sx={{ minWidth: isMobile ? "auto" : 240 }}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: "#666",
                        mb: 0.25,
                      }}
                    >
                      From Date
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <DatePicker
                          selected={fromDate}
                          onChange={(d) => setFromDate(d)}
                          onCalendarOpen={() => setIsPickingDate(true)}
                          onCalendarClose={() => setIsPickingDate(false)}
                          dateFormat="dd-MM-yyyy"
                          customInput={<DateInput />}
                        />
                      </Box>

                      <TimeSelector value={fromDate} onChange={setFromDate} />
                    </Box>
                  </Box>

                  {/* DIVIDER (Desktop only) */}
                  {!isMobile && (
                    <Box
                      sx={{
                        width: 1,
                        height: 36,
                        background: "rgba(255, 255, 255, 0)",
                        borderRadius: 2,
                      }}
                    />
                  )}

                  {/* TO DATE */}
                  <Box sx={{ minWidth: isMobile ? "auto" : 240 }}>
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: "#666",
                        mb: 0.25,
                      }}
                    >
                      To Date
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <DatePicker
                          selected={toDate}
                          onChange={(d) => setToDate(d)}
                          onCalendarOpen={() => setIsPickingDate(true)}
                          onCalendarClose={() => setIsPickingDate(false)}
                          dateFormat="dd-MM-yyyy"
                          customInput={<DateInput />}
                        />
                      </Box>

                      <TimeSelector value={toDate} onChange={setToDate} />
                    </Box>
                  </Box>
                </Box>

                {/* RIGHT SIDE - INFO */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: isMobile ? "row" : "column",
                    alignItems: isMobile ? "center" : "flex-end",
                    justifyContent: "space-between",
                    gap: 1,
                    minWidth: isMobile ? "auto" : 160,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: "#073057",
                    }}
                  >
                    Started: {filteredStrategies[activeStrategy]?.startDate}
                  </Typography>

                  {/* Mobile: Open trades indicator */}
                  {isMobile && openTradesCount > 0 && (
                    <Chip
                      size="small"
                      label={`${openTradesCount} Open`}
                      sx={{
                        height: 24,
                        fontSize: 11,
                        fontWeight: 600,
                        background: "#fff3e0",
                        color: "#e65100",
                        animation: "pulse 1.5s infinite",
                        "@keyframes pulse": {
                          "0%": { transform: "scale(1)" },
                          "50%": { transform: "scale(1.05)" },
                          "100%": { transform: "scale(1)" },
                        },
                      }}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* 🔥 UNIFIED METRICS (ALL IN ONE ROW) */}
            {activeStrategy &&
              trades.length > 0 &&
              (() => {
                const realisedPnl = trades.reduce((sum, t) => {
                  const isClosed =
                    t.status?.toLowerCase() === "closed" ||
                    (t.exit_time !== null && t.exit_time !== undefined);
                  if (!isClosed) return sum;
                  return sum + Number(t.pnl || 0);
                }, 0);

                const metrics = [
                  {
                    label: "NIFTY SPOT",
                    value:
                      niftyData.ltp !== null
                        ? Number(niftyData.ltp).toLocaleString("en-IN", {
                            maximumFractionDigits: 2,
                          })
                        : "-",

                    color: "#ffffff",

                    subtitle: getNiftyStatus().label,

                    bg: "linear-gradient(135deg, #426dda, #517abc)",

                    border: getNiftyStatus().color,
                  },
                  {
                    label: "P&L",
                    value: `${realisedPnl > 0 ? "+" : ""}${realisedPnl.toFixed(2)}`,
                    color:
                      realisedPnl > 0
                        ? "#2e7d32"
                        : realisedPnl < 0
                          ? "#c62828"
                          : "#555",
                  },
                  {
                    label: "Unrealised",
                    value: totalUnrealisedPnl.toFixed(2),
                    color:
                      totalUnrealisedPnl > 0
                        ? "#2e7d32"
                        : totalUnrealisedPnl < 0
                          ? "#c62828"
                          : "#555",
                    hide: !hasUnrealisedColumn,
                  },
                  {
                    label: "NIFTY",
                    value: totalSpotGain.toFixed(0) + " pts",
                    color:
                      totalSpotGain > 0
                        ? "#2e7d32"
                        : totalSpotGain < 0
                          ? "#c62828"
                          : "#555",
                  },

                  // 🔥 NEW CARDS (MERGED HERE)
                  ...(isBuyStrategy
                    ? [
                        {
                          label: "Option LTP",
                          value: netOptionLtp.toFixed(2),
                          color: "#101278",
                        },
                        {
                          label: "NIFTY Ratio",
                          value: `${niftyOptionRatio}%`,
                          color: "#101278",
                        },
                      ]
                    : []),

                  {
                    label: "Trades",
                    value: trades.length,
                    color: "#101278",
                  },
                ];

                return (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "repeat(2, 1fr)"
                        : "repeat(auto-fit, minmax(160px, 1fr))",
                      gap: isMobile ? 1.5 : 2,
                      mb: 2,
                    }}
                  >
                    {metrics
                      .filter((m) => !m.hide)
                      .map((m) => (
                        <Box
                          key={m.label}
                          sx={{
                            p: isMobile ? 1.0 : 1.2,
                            borderRadius: 3,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",

                            background: m.bg || "rgba(255,255,255,0.7)",
                            backdropFilter: "blur(16px)",
                            border: `1px solid ${m.border || "rgba(255,255,255,0.4)"}`,

                            boxShadow:
                              m.color === "#2e7d32"
                                ? "0 4px 16px rgba(46,125,50,0.2)"
                                : m.color === "#c62828"
                                  ? "0 4px 16px rgba(198,40,40,0.2)"
                                  : "0 4px 16px rgba(0,0,0,0.08)",

                            transition: "all 0.25s cubic-bezier(0.22,1,0.36,1)",
                            willChange: "transform, box-shadow",
                            transform: "translateZ(0)",

                            // ✅ DESKTOP ONLY HOVER
                            ...(!isMobile && {
                              "&:hover": {
                                transform: "translateY(-4px)",
                                boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                              },
                            }),

                            // ✅ MOBILE TAP FEEDBACK
                            ...(isMobile && {
                              "&:active": {
                                transform: "scale(0.97)",
                              },
                            }),
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: m.bg ? "rgba(255,255,255,0.75)" : "#666",
                              mb: 0.2,
                            }}
                          >
                            {m.label}
                          </Typography>
                          <>
                            <Typography
                              sx={{
                                fontSize: isMobile ? 16 : 19,
                                fontWeight: 700,
                                color: m.color,
                                lineHeight: 1.1,
                              }}
                            >
                              {m.value}
                            </Typography>

                            {m.subtitle && (
                              <Typography
                                sx={{
                                  fontSize: 9,
                                  fontWeight: 700,
                                  color: m.color,
                                  mt: 0.4,
                                  letterSpacing: 0.4,
                                  textTransform: "uppercase",
                                }}
                              >
                                {m.subtitle}
                              </Typography>
                            )}
                          </>
                        </Box>
                      ))}
                  </Box>
                );
              })()}
          </Box>
        )}
        {/* Analytics Accordion */}
        {(analytics || tradeInsights) && (
          <Accordion
            expanded={analyticsOpen}
            onChange={() => setAnalyticsOpen(!analyticsOpen)}
            sx={{
              mb: 2,
              borderRadius: 3,
              overflow: "hidden",
              background: "rgba(255,255,255,0.6)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.4)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                📊 Analytics
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {analytics && (
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "1fr 1fr"
                        : "repeat(3, 1fr)",
                      gap: 1.5,
                    }}
                  >
                    {[
                      {
                        label: "Win Rate",
                        value: analytics.winRate.toFixed(1) + "%",
                      },
                      {
                        label: "Avg Win",
                        value: "₹" + analytics.avgWin.toFixed(0),
                      },
                      {
                        label: "Avg Loss",
                        value: "₹" + analytics.avgLoss.toFixed(0),
                      },
                      {
                        label: "Profit Factor",
                        value: analytics.profitFactor.toFixed(2),
                      },
                      { label: "Sharpe", value: analytics.sharpe.toFixed(2) },
                      {
                        label: "Max DD",
                        value: "₹" + analytics.maxDrawdown.toFixed(0),
                      },
                    ].map((m) => (
                      <Box
                        key={m.label}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          background: "rgba(255,255,255,0.5)",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 10, color: "#888", fontWeight: 600 }}
                        >
                          {m.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 700,
                            color: "#073057",
                          }}
                        >
                          {m.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {tradeInsights && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.5,
                  }}
                >
                  {[
                    {
                      label: "Best Trade",
                      value: `₹${tradeInsights.bestTrade.toFixed(0)}`,
                      color: "#2e7d32",
                    },
                    {
                      label: "Worst Trade",
                      value: `₹${tradeInsights.worstTrade.toFixed(0)}`,
                      color: "#c62828",
                    },
                    {
                      label: "Win Streak",
                      value: tradeInsights.winStreak,
                      color: "#1976d2",
                    },
                    {
                      label: "Loss Streak",
                      value: tradeInsights.lossStreak,
                      color: "#ef6c00",
                    },
                  ].map((m) => (
                    <Box
                      key={m.label}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        background: "rgba(255,255,255,0.5)",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 10, color: "#888", fontWeight: 600 }}
                      >
                        {m.label}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 16, fontWeight: 700, color: m.color }}
                      >
                        {m.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Straddle Analytics */}
        {filteredStrategies[activeStrategy]?.straddle && (
          <Accordion
            expanded={straddleAnalyticsOpen}
            onChange={() => setStraddleAnalyticsOpen(!straddleAnalyticsOpen)}
            sx={{
              mb: 2,
              borderRadius: 3,
              overflow: "hidden",
              background: "rgba(255,255,255,0.85)",
              backdropFilter: "blur(12px)",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 16,
                }}
              >
                🎯 Straddle Analytics
              </Typography>
            </AccordionSummary>

            <AccordionDetails>
              {straddleAnalyticsLoading ? (
                <Loader />
              ) : (
                <>
                  {/* SUMMARY */}

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    {[
                      {
                        label: "Net P&L",
                        value: straddleAnalytics?.summary?.net_pnl,
                      },

                      {
                        label: "Total Straddles",
                        value: straddleAnalytics?.summary?.total_straddles,
                      },

                      {
                        label: "Premium Collected",
                        value: straddleAnalytics?.summary?.premium_collected,
                      },

                      {
                        label: "Open Legs",
                        value: straddleAnalytics?.summary?.open_legs,
                      },

                      {
                        label: "Best Leg",
                        value: straddleAnalytics?.summary?.best_leg,
                      },

                      {
                        label: "Worst Leg",
                        value: straddleAnalytics?.summary?.worst_leg,
                      },
                    ].map((card) => (
                      <Paper
                        key={card.label}
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#777",
                          }}
                        >
                          {card.label}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 22,
                            fontWeight: 700,
                            color:
                              card.value > 0
                                ? "#2e7d32"
                                : card.value < 0
                                  ? "#c62828"
                                  : "#073057",
                          }}
                        >
                          {Number(card.value || 0).toLocaleString()}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>

                  {/* EXPIRY TABLE */}

                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 700,
                      mb: 2,
                    }}
                  >
                    Expiry Analytics
                  </Typography>

                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Expiry</TableCell>
                          <TableCell align="right">Straddles</TableCell>
                          <TableCell align="right">Premium</TableCell>
                          <TableCell align="right">Net P&L</TableCell>
                          <TableCell align="right">ROI %</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {straddleAnalytics?.expiry_analytics?.map((row) => (
                          <TableRow key={row.expiry}>
                            <TableCell>{row.expiry}</TableCell>

                            <TableCell align="right">
                              {row.total_straddles}
                            </TableCell>

                            <TableCell align="right">
                              {Number(row.premium_collected).toFixed(0)}
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                color: row.net_pnl > 0 ? "#2e7d32" : "#c62828",
                                fontWeight: 700,
                              }}
                            >
                              {Number(row.net_pnl).toFixed(0)}
                            </TableCell>

                            <TableCell align="right">{row.roi_pct}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* CE vs PE Analytics */}
                  <Typography
                    sx={{ fontSize: 16, fontWeight: 700, mt: 4, mb: 2 }}
                  >
                    CE vs PE Analytics
                  </Typography>

                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Option</TableCell>
                          <TableCell align="right">Trades</TableCell>
                          <TableCell align="right">Premium</TableCell>
                          <TableCell align="right">PnL</TableCell>
                          <TableCell align="right">Avg PnL</TableCell>
                          <TableCell align="right">Best</TableCell>
                          <TableCell align="right">Worst</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {straddleAnalytics?.ce_vs_pe?.map((row) => (
                          <TableRow key={row.option_type}>
                            <TableCell>
                              <Chip
                                label={row.option_type}
                                color={
                                  row.option_type === "CE" ? "success" : "error"
                                }
                                size="small"
                              />
                            </TableCell>

                            <TableCell align="right">{row.trades}</TableCell>

                            <TableCell align="right">
                              {Number(row.premium_collected).toFixed(0)}
                            </TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                color: row.total_pnl > 0 ? "green" : "red",
                                fontWeight: 700,
                              }}
                            >
                              {Number(row.total_pnl).toFixed(0)}
                            </TableCell>

                            <TableCell align="right">
                              {Number(row.avg_pnl).toFixed(0)}
                            </TableCell>

                            <TableCell align="right">
                              {Number(row.best_trade).toFixed(0)}
                            </TableCell>

                            <TableCell align="right">
                              {Number(row.worst_trade).toFixed(0)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Win Rate Analytics */}
                  <Typography
                    sx={{ fontSize: 16, fontWeight: 700, mt: 4, mb: 2 }}
                  >
                    Win Rate Analytics
                  </Typography>

                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Expiry</TableCell>
                          <TableCell align="right">Straddles</TableCell>
                          <TableCell align="right">Winners</TableCell>
                          <TableCell align="right">Win Rate</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {straddleAnalytics?.winrate?.map((row) => (
                          <TableRow key={row.expiry}>
                            <TableCell>{row.expiry}</TableCell>

                            <TableCell align="right">
                              {row.total_straddles}
                            </TableCell>

                            <TableCell align="right">{row.winners}</TableCell>

                            <TableCell
                              align="right"
                              sx={{
                                color:
                                  row.win_rate >= 60
                                    ? "green"
                                    : row.win_rate >= 40
                                      ? "#f57c00"
                                      : "red",
                                fontWeight: 700,
                              }}
                            >
                              {row.win_rate}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Straddle Level Analytics */}
                  <Typography
                    sx={{ fontSize: 16, fontWeight: 700, mt: 4, mb: 2 }}
                  >
                    Straddle Level Analytics
                  </Typography>

                  <TableContainer
                    component={Paper}
                    sx={{
                      maxHeight: 500,
                      overflow: "auto",
                    }}
                  >
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Expiry</TableCell>
                          <TableCell>Strike</TableCell>
                          <TableCell align="right">Premium</TableCell>
                          <TableCell align="right">Decay %</TableCell>
                          <TableCell align="right">CE PnL</TableCell>
                          <TableCell align="right">PE PnL</TableCell>
                          <TableCell align="right">Net PnL</TableCell>
                          <TableCell align="right">ROI %</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Result</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {straddleAnalytics?.straddle_analytics?.map(
                          (row, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{row.trade_date}</TableCell>

                              <TableCell>{row.expiry}</TableCell>

                              <TableCell>{row.strike}</TableCell>

                              <TableCell align="right">
                                {Number(row.premium_collected).toFixed(0)}
                              </TableCell>

                              <TableCell align="right">
                                {row.premium_decay_pct}%
                              </TableCell>

                              <TableCell
                                align="right"
                                sx={{
                                  color: row.ce_pnl > 0 ? "green" : "red",
                                }}
                              >
                                {Number(row.ce_pnl).toFixed(0)}
                              </TableCell>

                              <TableCell
                                align="right"
                                sx={{
                                  color: row.pe_pnl > 0 ? "green" : "red",
                                }}
                              >
                                {Number(row.pe_pnl).toFixed(0)}
                              </TableCell>

                              <TableCell
                                align="right"
                                sx={{
                                  color: row.net_pnl > 0 ? "green" : "red",
                                  fontWeight: 700,
                                }}
                              >
                                {Number(row.net_pnl).toFixed(0)}
                              </TableCell>

                              <TableCell align="right">
                                {row.roi_pct}%
                              </TableCell>

                              <TableCell>
                                <Chip
                                  label={row.straddle_status}
                                  color={
                                    row.straddle_status === "OPEN"
                                      ? "warning"
                                      : "success"
                                  }
                                  size="small"
                                />
                              </TableCell>

                              <TableCell>
                                <Chip
                                  label={row.result}
                                  color={
                                    row.result === "WIN"
                                      ? "success"
                                      : row.result === "LOSS"
                                        ? "error"
                                        : "default"
                                  }
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          ),
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Trades Section */}
        {loading ? (
          <Loader loading={true} />
        ) : trades.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              background: "rgba(255,255,255,0.6)",
              borderRadius: 3,
            }}
          >
            <Typography sx={{ fontSize: 48, mb: 1 }}>📭</Typography>
            <Typography sx={{ color: "#666" }}>No trades found</Typography>
          </Box>
        ) : (
          <>
            {/* Mobile View Mode Toggle */}
            {isMobile && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Button
                  size="small"
                  onClick={() => setMobileViewMode("cards")}
                  sx={{
                    flex: 1,
                    py: 1,
                    borderRadius: 2,
                    fontSize: 12,
                    fontWeight: 600,
                    background:
                      mobileViewMode === "cards"
                        ? "linear-gradient(135deg, #1976d2, #42a5f5)"
                        : "rgba(255,255,255,0.8)",
                    color: mobileViewMode === "cards" ? "#fff" : "#666",
                    border: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  Cards View
                </Button>
                <Button
                  size="small"
                  onClick={() => setMobileViewMode("table")}
                  sx={{
                    flex: 1,
                    py: 1,
                    borderRadius: 2,
                    fontSize: 12,
                    fontWeight: 600,
                    background:
                      mobileViewMode === "table"
                        ? "linear-gradient(135deg, #1976d2, #42a5f5)"
                        : "rgba(255,255,255,0.8)",
                    color: mobileViewMode === "table" ? "#fff" : "#666",
                    border: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  Table View
                </Button>
              </Box>
            )}

            {/* Mobile Card View */}
            {isMobile && mobileViewMode === "cards" && (
              <Box sx={{ mb: 2 }}>
                {trades.slice(0, 50).map((trade, index) => (
                  <MobileTradeCard
                    key={trade.id || index}
                    trade={trade}
                    formatDateTime={formatDateTimeShort}
                  />
                ))}
                {trades.length > 50 && (
                  <Typography
                    sx={{
                      textAlign: "center",
                      color: "#666",
                      fontSize: 12,
                      py: 2,
                    }}
                  >
                    Showing 50 of {trades.length} trades. Switch to Table View
                    for all.
                  </Typography>
                )}
              </Box>
            )}

            {/* Table View (Desktop or Mobile Table Mode) */}
            {(!isMobile || mobileViewMode === "table") && (
              <Accordion
                expanded={tableOpen}
                onChange={() => setTableOpen(!tableOpen)}
                sx={{
                  mb: 2,
                  borderRadius: 3,
                  overflow: "hidden",
                  background: "rgba(255,255,255,0.6)",
                  backdropFilter: "blur(16px)",
                  border: "1px solid rgba(255,255,255,0.4)",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                    Trades ({trades.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: isMobile ? 1 : 2 }}>
                  <TableContainer
                    ref={tableRef}
                    component={Paper}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={stopAutoScroll}
                    sx={{
                      borderRadius: 2,
                      maxHeight: isMobile ? "400px" : "520px",
                      background: "rgba(255,255,255,0.8)",
                      overflow: "auto",
                      WebkitOverflowScrolling: "touch",
                      "&::-webkit-scrollbar": { height: 6, width: 6 },
                      "&::-webkit-scrollbar-thumb": {
                        background: "rgba(0,0,0,0.15)",
                        borderRadius: 3,
                      },
                    }}
                  >
                    <Table stickyHeader size={isMobile ? "small" : "medium"}>
                      <TableHead>
                        <TableRow>
                          {Object.keys(trades?.[0] || {}).map((key) => (
                            <TableCell
                              key={key}
                              sx={{
                                fontWeight: 700,
                                fontSize: isMobile ? 10 : 12,
                                color: "#073057",
                                background: "rgba(255,255,255,0.9)",
                                whiteSpace: "nowrap",
                                py: isMobile ? 1 : 1.5,
                              }}
                            >
                              {key}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {trades.map((row, index) => (
                          <VirtualRow
                            key={row.id || index}
                            index={index}
                            style={{}}
                            data={{
                              rows: trades,
                              columns: Object.keys(trades?.[0] || {}),
                              formatDateTime: isMobile
                                ? formatDateTimeShort
                                : formatDateTime,
                            }}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Pagination */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Pagination
                count={Math.ceil(totalCount / limit)}
                page={page}
                onChange={(e, val) => setPage(val)}
                size={isMobile ? "small" : "medium"}
                sx={{
                  "& .MuiPaginationItem-root": {
                    background: "rgba(255,255,255,0.8)",
                  },
                }}
              />
            </Box>
          </>
        )}

        {/* Charts */}
        {dailyPnlData.length > 0 && (
          <Accordion
            expanded={dailyChartOpen}
            onChange={() => setDailyChartOpen(!dailyChartOpen)}
            sx={{
              mb: 2,
              borderRadius: 3,
              overflow: "hidden",
              background: "rgba(255,255,255,0.6)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.4)",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                📈 Daily Performance
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
                <AreaChart data={dailyPnlData}>
                  <defs>
                    <linearGradient
                      id="profitGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#2e7d32" stopOpacity={0.6} />
                      <stop offset="95%" stopColor="#2e7d32" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    angle={-35}
                    textAnchor="end"
                    height={60}
                    tick={{ fontSize: isMobile ? 9 : 11 }}
                  />
                  <YAxis
                    tickFormatter={(v) =>
                      Math.abs(v) >= 1000
                        ? `₹${(v / 1000).toFixed(1)}k`
                        : `₹${v}`
                    }
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                  />
                  <ReferenceLine y={0} stroke="#999" strokeDasharray="3 3" />
                  <RechartsTooltip
                    formatter={(value) => [
                      `₹${Number(value || 0).toFixed(2)}`,
                      "P&L",
                    ]}
                  />
                  <Area
                    type="natural"
                    dataKey="pnl"
                    stroke="#1976d2"
                    fill="url(#profitGradient)"
                    strokeWidth={2}
                    dot={{ r: isMobile ? 2 : 3 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </AccordionDetails>
          </Accordion>
        )}

        {pnlChartData.length > 0 && (
          <Accordion
            expanded={chartOpen}
            onChange={() => setChartOpen(!chartOpen)}
            sx={{
              mb: 2,
              borderRadius: 3,
              overflow: "hidden",
              background: "rgba(255,255,255,0.6)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.4)",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                📉 Equity Curve
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                <AreaChart data={pnlChartData}>
                  <defs>
                    <linearGradient
                      id="equityPositive"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#2e7d32" stopOpacity={0.6} />
                      <stop offset="95%" stopColor="#2e7d32" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="index"
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    label={
                      isMobile
                        ? undefined
                        : {
                            value: "Trades",
                            position: "insideBottom",
                            offset: -5,
                          }
                    }
                  />
                  <YAxis
                    tickFormatter={(v) =>
                      Math.abs(v) >= 1000
                        ? `₹${(v / 1000).toFixed(1)}k`
                        : `₹${v}`
                    }
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                  />
                  <ReferenceLine y={0} stroke="#999" strokeDasharray="3 3" />
                  <RechartsTooltip
                    formatter={(value) => [
                      `₹${Number(value || 0).toFixed(2)}`,
                      "P&L",
                    ]}
                  />
                  <Area
                    type="monotone"
                    dataKey="cumulativePnl"
                    stroke="#1976d2"
                    fill="url(#equityPositive)"
                    strokeWidth={2}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </Box>
  );
}

export default EliteStrategiesTradeLogs;
